import { trackError } from '@billy/track-error'

import type { TradeName } from '../../../types/companyValues'
import type { InvoicingSettingsPreviewPatch } from '../../../types/invoicingSettingsPreviewPatch'
import type { ZervantSettings } from '../../../types/zervantSettings'
import { getPrimaryTradeName } from '../../../utils/getPrimaryTradeName'
import { getInvoicingSettingsPayload } from '../../../writer/utils/getInvoicingSettingsPayload'

type PreviewLogo = NonNullable<InvoicingSettingsPreviewPatch['logo']>

const getTradeNameWithPreviewLogo = (
  tradeName: TradeName,
  primaryTradeName: TradeName | undefined,
  previewLogo: PreviewLogo,
): TradeName => {
  if (tradeName !== primaryTradeName) {
    return tradeName
  }

  const { logo, ...tradeNameWithoutLogo } = tradeName

  return {
    ...tradeNameWithoutLogo,
    ...(previewLogo.id ? { logo: previewLogo } : {}),
  }
}

const getSettingsWithPreviewLogo = (settings: ZervantSettings, previewLogo: PreviewLogo): ZervantSettings => {
  const company = settings.values.company
  const primaryTradeName = getPrimaryTradeName(company.tradeNames)

  return {
    ...settings,
    values: {
      ...settings.values,
      company: {
        ...company,
        tradeNames: company.tradeNames.map((tradeName) =>
          getTradeNameWithPreviewLogo(tradeName, primaryTradeName, previewLogo),
        ),
      },
    },
  }
}

// A missing model type or field is a save-time contract violation (see getInvoicingSettingsPayload),
// so the writer throws loudly there. The preview runs that same check on every keystroke, during
// render, where a throw would crash the modal instead of showing a toast. Fall back to the saved
// settings and report it, rather than let an edge case the module didn't anticipate take the preview down.
const getPatchedSettings = (
  zervantSettings: ZervantSettings,
  settingsPatch: InvoicingSettingsPreviewPatch['settings'],
): ZervantSettings => {
  if (!settingsPatch) {
    return zervantSettings
  }

  try {
    return getInvoicingSettingsPayload({ cachedSettings: zervantSettings, patch: settingsPatch })
  } catch (error) {
    trackError(error)

    return zervantSettings
  }
}

export const getUnsavedZervantSettings = (
  zervantSettings: ZervantSettings,
  previewPatch?: InvoicingSettingsPreviewPatch,
): ZervantSettings => {
  const patchedSettings = getPatchedSettings(zervantSettings, previewPatch?.settings)

  // Omitted logo keeps the saved one. A reported logo replaces it, and an empty id removes it.
  if (!previewPatch?.logo) {
    return patchedSettings
  }

  return getSettingsWithPreviewLogo(patchedSettings, previewPatch.logo)
}
